/**
 * Booking type filter for the Actuals pages.
 *
 * The two sources split bookings differently, so each has its own chip set:
 *
 *  - TBN stores a raw booking type on the record as `BookingType`
 *    ("Booking" / "ScheduledService" / "SchoolContract"); the actuals rows carry
 *    it lowercased as `bookingType` (and `type`).
 *  - Coach Manager has no such field to trust here -- its `bookingType` is a
 *    free-form tenant value (Charter, Transfer, ...) already served by the Type
 *    dropdown. The charter/contract split is structural instead: a booking is a
 *    PrivateHire or a Contract, which the row carries as `type` and which is
 *    also the prefix of the `bookingID` hash ('private_hire_9_93392' /
 *    'contract_131_2374_20260715').
 */
const CHARTER = 'charter'
const SCHEDULED_SERVICE = 'scheduled_service'
const SCHOOL_BUS = 'school_bus'
const CONTRACT = 'contract'

const TBN_RAW_TYPE_STATES = {
  booking: CHARTER,
  privatehire: CHARTER,
  charter: CHARTER,
  scheduledservice: SCHEDULED_SERVICE,
  schoolcontract: SCHOOL_BUS,
  schoolbus: SCHOOL_BUS,
}

const CM_RAW_TYPE_STATES = {
  privatehire: CHARTER,
  contract: CONTRACT,
}

/** Chip definitions per source, in display order. */
export const ACTUALS_BOOKING_TYPE_FILTERS = {
  tbn: [
    { value: CHARTER, label: 'Charter' },
    { value: SCHEDULED_SERVICE, label: 'SS' },
    { value: SCHOOL_BUS, label: 'SB' },
  ],
  cm: [
    { value: CHARTER, label: 'Charters' },
    { value: CONTRACT, label: 'Contracts' },
  ],
}

export function actualsBookingTypeFilters(source = 'tbn') {
  return ACTUALS_BOOKING_TYPE_FILTERS[source] || ACTUALS_BOOKING_TYPE_FILTERS.tbn
}

function normalize(raw) {
  if (raw == null || raw === '')
    return null
  return String(raw).toLowerCase().replace(/[^a-z]/g, '')
}

/** Booking type state for an actuals row, in the vocabulary of its source. */
export function actualsBookingTypeState(item, source = 'tbn') {
  if (!item || item._overlap_group_header)
    return null
  if (source === 'cm') {
    const state = CM_RAW_TYPE_STATES[normalize(item.type)]
    if (state)
      return state
    // no `type` on the row: fall back to the hash prefix VSM filters on
    const hash = item.bookingID == null ? '' : String(item.bookingID)
    if (hash.indexOf('private_hire_') === 0)
      return CHARTER
    if (hash.indexOf('contract_') === 0)
      return CONTRACT
    return null
  }
  const raw = item.bookingType != null && item.bookingType !== '' ? item.bookingType : item.type
  return TBN_RAW_TYPE_STATES[normalize(raw)] || null
}

export function matchesActualsBookingTypeFilter(item, selectedType, source = 'tbn') {
  if (!selectedType || !actualsBookingTypeFilters(source).some(opt => opt.value === selectedType))
    return true
  if (item && item._overlap_group_header)
    return false
  return actualsBookingTypeState(item, source) === selectedType
}

export function countActualsBookingType(items, bookingType, source = 'tbn') {
  return (items || []).filter(item => actualsBookingTypeState(item, source) === bookingType).length
}

/** Hover help for booking type filter chips on the Actuals page. */
export const ACTUALS_BOOKING_TYPE_FILTER_TOOLTIPS = {
  tbn: {
    group: 'Filter the table by booking type: Charter, SS (scheduled service), SB (school bus). Counts reflect the current date range and status filters (before the booking type filter is applied).',
    [CHARTER]: 'Show charter (private hire) bookings. Click the chip to filter; click again to clear.',
    [SCHEDULED_SERVICE]: 'SS — show scheduled service (line run / contract) bookings. Click the chip to filter; click again to clear.',
    [SCHOOL_BUS]: 'SB — show school bus (school contract) bookings. Click the chip to filter; click again to clear.',
  },
  cm: {
    group: 'Filter the table by booking type: Charters (private hires) or Contracts (line runs). Counts reflect the current date range and status filters (before the booking type filter is applied).',
    [CHARTER]: 'Show charters (private hire bookings). Click the chip to filter; click again to clear.',
    [CONTRACT]: 'Show contracts (line run bookings). Click the chip to filter; click again to clear.',
  },
}

export function actualsBookingTypeTooltips(source = 'tbn') {
  return ACTUALS_BOOKING_TYPE_FILTER_TOOLTIPS[source] || ACTUALS_BOOKING_TYPE_FILTER_TOOLTIPS.tbn
}
